import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
  useTheme,
  Card,
} from "@mui/material";
import { tokens } from "../../../theme";
import { Formik } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";
import Header from "../../../components/Header";
import DynamicForm from "../../../data/Axios/DynamicForm";
import Swal from "sweetalert2";
import {
  allReportMinorClassifications,
  allReportsMajorClassifications,
  allReportTypes,
} from "../../../data/Axios/queries";
import {
  setupManagementUrl,
  reportsManagementUrl,
  emailService,
  companyManagementUrl,
  userManagementUrl,
} from "../../../config";
import { generateReport } from "../../../data/DynamicTable/DynamicFunctions";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../data/DynamicServiceNew/jsondynamicservice";
import Joyride, { STATUS } from "react-joyride";

const base_url = reportsManagementUrl.uri;
// const base_url = 'https://localhost:7056/graphql/'
const baseUrl2 = companyManagementUrl.uri;
const baseUrl3 = userManagementUrl.uri;

const Filter = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);
  const branchId = parseInt(decodedToken.BranchId);
  const RoleGroupId = parseInt(decodedToken.RoleGroupId);
  const regionId = parseInt(decodedToken.RegionId);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [major, setMajor] = useState([]);
  const [minor, setMinor] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [reportName, setReportName] = useState([]);
  const [asAt, setAsAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [filteredMinorOptions, setFilteredMinorOptions] = useState([]);
  const [filteredReportTypesOptions, setFilteredReportTypesOptions] = useState(
    [],
  );
  const [regionData, setRegionData] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [FOData, setFOData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedFO, setSelectedFO] = useState(null);
  const [reportDateOption, setReportDateOption] = useState(null); // Track selected Report Date option
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showAsAt, setShowAsAt] = useState(false); // Control visibility of "As At"
  const [showReportDate, setShowReportDate] = useState(true);
  const [selectedReportTypeID, setSelectedReportTypeID] = useState(null);
  const [selectedMajorId, setSelectedMajorId] = useState(
    props.data ? props.data.majorClassificationID : "",
  );
  const [selectedMinorId, setSelectedMinorId] = useState(
    props.data ? props.data.minorClassificationID : "",
  );
  const [runTour, setRunTour] = useState(false);

  const steps = [
    {
      target: '[name="MajorClassificationID"]',
      content: "Select a major classification.",
    },
    {
      target: '[name="MinorClassificationID"]',
      content: "Select a minor classification.",
    },
    {
      target: '[name="reportTypeID"]',
      content: "Pick the report type you want to generate.",
    },
    {
      target: '[name="reportDateOption"]',
      content: "Choose a date filter or range for your report.",
    },
    {
      target: '[name="StartDate"]',
      content: "Pick the custom start date (if applicable).",
    },
    {
      target: '[name="EndDate"]',
      content: "Pick the custom end date (if applicable).",
    },
    {
      target: '[name="asAt"]',
      content: "Set the 'As at' date for locked reports.",
    },
  ];

  const reportDateOptions = [
    { parent_key: null, value: 1, label: "Today" },
    { parent_key: null, value: 7, label: "Tomorrow" },
    { parent_key: null, value: 2, label: "Yesterday" },
    { parent_key: null, value: 3, label: "Last 30 Days" },
    { parent_key: null, value: 4, label: "This Month" },
    { parent_key: null, value: 5, label: "Last Month" },
    { parent_key: null, value: 6, label: "Custom Range" },
  ];

  const limitedDateReportTypes = [48, 49, 54];
  const incomeStatementReportTypes = [55, 56]; // Summarized and Condensed Income Statement
  useEffect(() => {
    setAsAt(dayjs().format("YYYY-MM-DD"));
  }, []);

  // Ensure the asAt value always resets to the current date if changed
  useEffect(() => {
    if (asAt !== dayjs().format("YYYY-MM-DD")) {
      setAsAt(dayjs().format("YYYY-MM-DD"));
    }
  }, [asAt]);

  // Fetch dropdown data (Regions, Branches, Field Officers)
  useEffect(() => {
    async function fetchDropdownData() {
      try {
        const rolegroup = { isActive: 1, roleGroupId: 5 };
        const [res1, res2, res3] = await Promise.all([
          fetchJsonDynamic("region", null, null, baseUrl2),
          fetchJsonDynamic("branch", null, null, baseUrl2),
          fetchJsonDynamic(
            "allUsersByColumnValues",
            "usermodel",
            rolegroup,
            baseUrl3,
          ),
        ]);

        setRegionData(res1?.results ? JSON.parse(res1.results) : []);
        setBranchData(res2?.results ? JSON.parse(res2.results) : []);
        const allUsers = res3?.results ? JSON.parse(res3.results) : [];
        const filteredFOData = allUsers.filter(
          (user) => user.RoleGroupId === 5,
        );
        setFOData(filteredFOData);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchDropdownData();
  }, []);

  // Fetch report classifications and types
  useEffect(() => {
    async function fetchAllData() {
      try {
        const minorReportsDataResponse = await fetchJsonDynamic(
          "reportsMinorClassifications",
          null,
          null,
          base_url,
        );
        if (minorReportsDataResponse?.results) {
          const minor = JSON.parse(minorReportsDataResponse?.results);
          setMinor(minor || []);
        }
        const majorReportsDataResponse = await fetchJsonDynamic(
          "reportsMajorClassifications",
          null,
          null,
          base_url,
        );
        if (majorReportsDataResponse?.results) {
          const results = JSON.parse(majorReportsDataResponse?.results);
          setMajor(results || []);
        }
        const reportTypesDataResponse = await fetchJsonDynamic(
          "reportTypes",
          null,
          null,
          base_url,
        );
        if (reportTypesDataResponse?.results) {
          const results = JSON.parse(reportTypesDataResponse?.results);
          setReportTypes(results || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, []);

  // Set default Report Date to "Last 30 Days" for ReportTypeId 55 and 56
  useEffect(() => {
    if (incomeStatementReportTypes.includes(selectedReportTypeID)) {
      const last30DaysOption = reportDateOptions.find(
        (option) => option.value === 3,
      );
      setReportDateOption(last30DaysOption);
      // Set default dates for Last 30 Days
      const today = dayjs();
      setStartDate(today.subtract(30, "days").format("YYYY-MM-DD"));
      setEndDate(today.format("YYYY-MM-DD"));
    } else {
      setReportDateOption(null); // Reset for other report types
      setStartDate(null);
      setEndDate(null);
    }
  }, [selectedReportTypeID]);

  const regionOptions =
    regionData?.map((item) => ({
      parent_key: item.Id,
      value: item.Id,
      label: item.Name,
    })) || [];

  const branchOptions =
    branchData?.map((item) => ({
      parent_key: item.RegionId,
      value: item.Id,
      label: item.Name,
    })) || [];

  const FOOptions =
    FOData?.map((item) => ({
      parent_key: item.BranchId,
      parent_key2: item.RegionId,
      value: item.Id,
      label: `${item.FirstName} ${item.LastName}`,
    })) || [];

  const executiveReports = [3, 1];

  const majorOptions = major
    ? major
        .filter((item) => {
          // If rolegroupid is 5, filter out executive reports
          if (RoleGroupId === 5 && companyId === 29) {
            return !executiveReports.includes(item.Id);
          }

          return true;
        })
        .map((item) => ({
          parent_key: item.id,
          value: item.Id,
          label: item.MajorClassification,
        }))
    : [];

  const lockedReportTypes = [15, 17, 37, 22, 23, 16, 29, 31, 32];

  const clearReportDateSelection = () => {
    setReportDateOption(null);
    setStartDate(null);
    setEndDate(null);
  };

  // DynamicForm's FormikObserver replays onFieldChange for every "main"
  // field whenever ANY of their values change (it needs this to preload
  // dependents in edit mode). That means Major/Minor's handlers below get
  // re-invoked with their unchanged value any time Report Type is picked.
  // These refs let us tell "value actually changed" apart from "replay
  // with the same value", so we only cascade-reset on a real change.
  const lastMajorRef = useRef(selectedMajorId);
  const lastMinorRef = useRef(selectedMinorId);

  const onFieldChange = async (field, value) => {
    let updatedOptions = {};
    if (field.name === "MajorClassificationID") {
      const majorChanged = lastMajorRef.current !== value;
      lastMajorRef.current = value;
      setSelectedMajorId(value);
      if (majorChanged) {
        // Changing the parent classification invalidates whatever minor
        // classification/report type/date range was previously chosen.
        lastMinorRef.current = "";
        setSelectedMinorId("");
        setSelectedReportTypeID(null);
        clearReportDateSelection();
      }

      const filteredMinorOptions = minor
        .filter((item) => item.MajorClassificationID === value)
        .map((item) => ({
          parent_key: item.MajorClassificationID,
          value: item.Id,
          label: item.MinorClassification,
        }));
      setFilteredReportTypesOptions([]);
      updatedOptions = {
        MinorClassificationID: filteredMinorOptions,
      };
      setFilteredMinorOptions(filteredMinorOptions);
    }

    if (field.name === "MinorClassificationID") {
      const minorChanged = lastMinorRef.current !== value;
      lastMinorRef.current = value;
      setSelectedMinorId(value);
      if (minorChanged) {
        // Changing the minor classification invalidates the previously
        // chosen report type and any date range tied to it.
        setSelectedReportTypeID(null);
        clearReportDateSelection();
      }

      const filteredReportTypesOptions = reportTypes
        .filter((item) => item.MinorClassificationID === value)
        .map((item) => ({
          parent_key: item.MinorClassificationID,
          value: item.Id,
          label: item.ReportName,
        }));
      updatedOptions = {
        ...updatedOptions,
        reportTypeID: filteredReportTypesOptions,
      };
      setFilteredReportTypesOptions(filteredReportTypesOptions);
    }

    if (field.name === "reportTypeID") {
      setSelectedReportTypeID(value);
      const isLocked = lockedReportTypes.includes(value);
      setShowAsAt(isLocked);
      setShowReportDate(!isLocked);
      if (isLocked) {
        setAsAt(dayjs().format("YYYY-MM-DD"));
      } else {
        setAsAt(null);
      }
    }

    return updatedOptions;
  };

  const getFilteredReportDateOptions = (reportTypeId) => {
    if (incomeStatementReportTypes.includes(reportTypeId)) {
      // Only show Last 30 Days, This Month, Last Month, Custom Range for ReportTypeId 55 and 56
      return reportDateOptions.filter((option) =>
        [3, 4, 5, 6].includes(option.value),
      );
    }
    if (limitedDateReportTypes.includes(reportTypeId)) {
      return reportDateOptions.filter(
        (option) => [1, 2, 3, 4, 5, 6, 7].includes(option.value), // Exclude Today, Yesterday, Tomorrow
      );
    }
    return reportDateOptions; // Default: show all options
  };

  const handleReportDateChange = (event, newValue) => {
    console.log("Received selection:", {
      rawValue: newValue,
      type: typeof newValue,
      isObject: typeof newValue === "object",
      value: newValue,
    });

    if (!newValue) {
      console.log("Selection cleared - resetting dates");
      setReportDateOption(null);
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const selectedValue =
      typeof newValue === "object" ? newValue.value : newValue;
    console.log("Processing selection value:", selectedValue);

    setReportDateOption(newValue);

    const today = dayjs();
    let startDate = null;
    let endDate = null;

    switch (selectedValue) {
      case 1: // Today
        startDate = today.format("YYYY-MM-DD");
        endDate = today.format("YYYY-MM-DD");
        break;
      case 2: // Yesterday
        startDate = today.subtract(1, "day").format("YYYY-MM-DD");
        endDate = today.subtract(1, "day").format("YYYY-MM-DD");
        break;
      case 3: // Last 30 Days
        startDate = today.subtract(30, "days").format("YYYY-MM-DD");
        endDate = today.format("YYYY-MM-DD");
        break;
      case 4: // This Month
        startDate = today.startOf("month").format("YYYY-MM-DD");
        endDate = today.endOf("month").format("YYYY-MM-DD");
        break;
      case 5: // Last Month
        startDate = today
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD");
        endDate = today
          .subtract(1, "month")
          .endOf("month")
          .format("YYYY-MM-DD");
        break;
      case 6: // Custom Range
        console.log("Custom range selected - dates will be set manually");
        break;
      case 7: // Tomorrow
        startDate = today.add(1, "day").format("YYYY-MM-DD");
        endDate = today.add(1, "day").format("YYYY-MM-DD");
        break;
      default:
        console.warn("Unknown selection value:", selectedValue);
    }

    if (selectedValue !== 6) {
      console.log("Setting dates:", { startDate, endDate });
      setStartDate(startDate);
      setEndDate(endDate);
    }
  };

  // Control when to show "As At" field
  useEffect(() => {
    const isLocked = lockedReportTypes.includes(selectedReportTypeID);
    setShowReportDate(!isLocked);
    setShowAsAt(isLocked);
  }, [selectedReportTypeID]);

  const fields = [
    {
      name: "MajorClassificationID",
      label: "Major Classification",
      type: "select",
      options: majorOptions,
      required: true,
      parent: "main",
      gridColumn: "span 1",
    },
    {
      name: "MinorClassificationID",
      label: "Minor Classification",
      type: "select",
      options: filteredMinorOptions,
      required: true,
      gridColumn: "span 1",
      parent: "main",
    },
    {
      name: "reportTypeID",
      label: "Report Type",
      type: "select",
      parent: "main",
      options: filteredReportTypesOptions,
      required: true,
      gridColumn: "span 1",
      onChange: (e) => setSelectedReportTypeID(e.target.value),
    },
    ...(showReportDate && !showAsAt
      ? [
          {
            name: "reportDateOption",
            label: "Report Date",
            type: "select",
            options: getFilteredReportDateOptions(selectedReportTypeID),
            required: false,
            gridColumn: "span 1",
            value: reportDateOption, // <-- Ensure value is controlled
            onChange: (event, newValue) => {
              console.log("Direct Autocomplete onChange:", newValue); // Debug log
              handleReportDateChange(event, newValue);
            },
            isOptionEqualToValue: (option, value) =>
              option.value === (value?.value ?? value),
            // Disable DynamicForm's internal onChange handling for this field
            skipInternalOnChange: true, // Add this flag
          },
        ]
      : []), // Hide Report Date when using As At or Custom Range

    // Only show date range fields when:
    // 1. Not using As At (showAsAt is false)
    // 2. Custom Range is selected (reportDateOption === 6)
    // 3. reportDateOption is not null
    ...(!showAsAt && reportDateOption === 6 && reportDateOption !== null
      ? [
          {
            name: "StartDate",
            label: "Start Date",
            type: "date",
            gridColumn: "span 1",
            value: startDate || "",
            onChange: (e) => setStartDate(e.target.value),
          },
          {
            name: "EndDate",
            label: "End Date",
            type: "date",
            gridColumn: "span 1",
            value: endDate || "",
            onChange: (e) => setEndDate(e.target.value),
          },
        ]
      : []),
    ...(showAsAt
      ? [
          {
            name: "asAt",
            label: "As at",
            type: "date",
            value: asAt,
            gridColumn: "span 1",
            required: true,
          },
        ]
      : []), // Show As At field when needed
  ];

  const initialValues = {
    MajorClassificationID: selectedMajorId,
    MinorClassificationID: selectedMinorId,
    reportTypeID: selectedReportTypeID,
    ReportName: props.data ? props.data.reportName : "",
    reportDateOption: reportDateOption,
    StartDate: startDate || null,
    EndDate: endDate || null,
    asAt,
  };

  const handleDateChange = (newDate) => {
    setAsAt(newDate);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { reportTypeID, asAt, reportDateOption } = values;
      const ReportType =
        reportTypes.find((item) => item.Id === reportTypeID)?.ReportName || "";

      const RegionId = selectedRegion?.value ?? regionId;
      const BranchId = selectedBranch?.value ?? branchId;
      const UserIdfromdropdown = selectedFO?.value;
      const UserIdfromtoken = userId;

      let roleGroupid = RoleGroupId;
      if (RegionId) roleGroupid = 3;
      if (RegionId && BranchId) roleGroupid = 4;
      if (RegionId && BranchId && UserIdfromdropdown) roleGroupid = 5;
      if (RoleGroupId === 5) roleGroupid = 5;

      let finalUserId = UserIdfromdropdown ?? UserIdfromtoken;
      if (roleGroupid === 1) {
        finalUserId = null;
      }

      const hasCustomDates =
        (startDate && endDate) || (values.StartDate && values.EndDate);

      const finalStartDate = hasCustomDates
        ? startDate || values.StartDate
        : asAt;

      const finalEndDate = hasCustomDates ? endDate || values.EndDate : asAt;

      const mutationData = {
        ReportTypeId: reportTypeID,
        RegionId: RegionId,
        BranchId: BranchId,
        UserId: finalUserId,
        roleGroupId: roleGroupid,
        ReportType,
        StartDate: finalStartDate,
        EndDate: finalEndDate,
      };

      const newTab = window.open("", "_blank");
      newTab.document.write(`
        <html>
          <head>
            <title>Loading...</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                background-color: #f0f0f0;
              }
              .loading {
                font-size: 24px;
                color: #333;
              }
            </style>
          </head>
          <body>
            <div class="loading">Loading, please wait...</div>
          </body>
        </html>
      `);
      newTab.document.close();

      const response = await generateReport(
        mutationData,
        reportsManagementUrl.generate_report_uri,
        setIsLoading,
      );

      if (response.ok) {
        const htmlContent = await response.text();
        newTab.document.open();
        newTab.document.write(htmlContent);
        newTab.document.close();
      } else {
        console.error("Error:", response.statusText);
        newTab.close();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
    setAsAt(dayjs().format("YYYY-MM-DD"));
    setStartDate(null);
    setEndDate(null);
    setSubmitting(false);
  };

  const handleFOChange = (event, newValue) => {
    setSelectedFO(newValue);
    if (newValue) {
      const branch = branchOptions.find(
        (branch) => branch.value === newValue.parent_key,
      );
      const region = regionOptions.find(
        (region) => region.value === newValue.parent_key2,
      );
      setSelectedBranch(branch || null);
      setSelectedRegion(region || null);
    }
  };

  const handleBranchChange = (event, newValue) => {
    setSelectedBranch(newValue);
    if (newValue) {
      const region = regionOptions.find(
        (region) => region.value === newValue.parent_key,
      );
      setSelectedRegion(region || null);
    }
  };

  return (
    <Box m="20px">
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        styles={{ options: { primaryColor: "#1976d2", zIndex: 10000 } }}
        callback={({ status }) => {
          if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            setRunTour(false);
          }
        }}
      />

      {(RoleGroupId === 1 ||
        RoleGroupId === 2 ||
        RoleGroupId === 3 ||
        RoleGroupId === 4) && (
        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={4} mb={1}>
          {(RoleGroupId === 1 || RoleGroupId === 2) && (
            <Autocomplete
              options={regionOptions}
              loading={loading}
              value={selectedRegion}
              onChange={(event, newValue) => setSelectedRegion(newValue)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.primary[300],
                  borderRadius: "8px",
                  color: colors.grey[100],
                  "&:hover": {
                    backgroundColor: colors.secondary,
                  },
                  "&.Mui-focused": {
                    borderColor: colors.accent?.[400] || "#FF5733",
                    boxShadow: `0px 0px 5px ${
                      colors.accent?.[400] || "#FF5733"
                    }`,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Region"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {(RoleGroupId === 1 || RoleGroupId === 2 || RoleGroupId === 3) && (
            <Autocomplete
              options={branchOptions}
              loading={loading}
              value={selectedBranch}
              onChange={handleBranchChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.primary[300],
                  borderRadius: "8px",
                  color: colors.grey[100],
                  "&:hover": {
                    backgroundColor: colors.secondary,
                  },
                  "&.Mui-focused": {
                    borderColor: colors.accent?.[400] || "#FF5733",
                    boxShadow: `0px 0px 5px ${
                      colors.accent?.[400] || "#FF5733"
                    }`,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Branch"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {(RoleGroupId === 1 ||
            RoleGroupId === 2 ||
            RoleGroupId === 3 ||
            RoleGroupId === 4) && (
            <Autocomplete
              options={FOOptions}
              loading={loading}
              value={selectedFO}
              onChange={handleFOChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.primary[300],
                  borderRadius: "8px",
                  color: colors.grey[100],
                  "&:hover": {
                    backgroundColor: colors.secondary,
                  },
                  "&.Mui-focused": {
                    borderColor: colors.accent?.[400] || "#FF5733",
                    boxShadow: `0px 0px 5px ${
                      colors.accent?.[400] || "#FF5733"
                    }`,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select RO"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="outlined" onClick={() => setRunTour(true)}>
              Start Quick Tour
            </Button>
          </Box>
        </Box>
      )}
      <Card m="10px" sx={{ background: `${colors.primary[400]} !important` }}>
        <DynamicForm
          fields={fields}
          onSubmit={handleSubmit}
          title="Report Browser"
          subtitle="Please select the report parameters"
          initialValues={initialValues}
          onFieldChange={onFieldChange}
        />
      </Card>
    </Box>
  );
};

export default Filter;
